import React, { useLayoutEffect, useRef, useState } from "react";
import { Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { Home, Receipt, HelpCircle, User, Search, ShoppingCart, ChevronDown, LogIn, MessageCircle } from "lucide-react";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/auth";
import BranchModal from "./BranchModal";
import AuthModal from "./AuthModal";
import TermsGate from "./TermsGate";
import { LOGO } from "@/lib/ellaApi";

const NAV = [
  { to: "/", label: "Beranda", icon: Home, testId: "nav-home" },
  { to: "/account/transactions", label: "Transaksi", icon: Receipt, testId: "nav-transactions", authRequired: true },
  { to: "/messages", label: "Pesan", icon: MessageCircle, testId: "nav-messages", authRequired: true },
  { to: "/help", label: "Bantuan", icon: HelpCircle, testId: "nav-help" },
  { to: "/account", label: "Akun", icon: User, testId: "nav-account", authRequired: true },
];

export default function AppShell() {
  const shellRef = useRef(null);
  const headerRef = useRef(null);
  useLayoutEffect(() => {
    const header = headerRef.current;
    const measure = () => shellRef.current?.style.setProperty("--app-header-height", `${header.getBoundingClientRect().height}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  const loc = useLocation();
  const nav = useNavigate();
  const { branch, cartCount } = useStore();
  const { isAuthed } = useAuth();
  const [showBranch, setShowBranch] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const isCheckout = loc.pathname.startsWith("/checkout") || loc.pathname.startsWith("/cart") || loc.pathname.startsWith("/product/") || /^\/messages\/[^/]+/.test(loc.pathname);
  const hideBottomNav = isCheckout;

  return (
    // Pages that hide the bottom nav (checkout, cart, product, chat) provide their
    // own bottom spacing, so skip the nav padding there — the chat needs the exact
    // viewport height to scroll internally.
    <div ref={shellRef} className={`app-shell ${hideBottomNav ? "" : "pb-24"}`}>
      {/* Top sticky header */}
      <header ref={headerRef} className="sticky top-0 z-40 glass border-b border-gray-100">
        <div className="px-4 pt-3 pb-2 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
            <img src={LOGO} alt="Ella Froze" className="h-8 w-auto" />
          </Link>
          <button
            onClick={() => setShowBranch(true)}
            className="shrink-0 flex items-center gap-1 text-xs font-medium px-2 py-1.5 rounded-full bg-red-50 text-[#D32F2F] hover:bg-red-100 transition"
            data-testid="branch-selector-button"
          >
            <span className="max-w-[72px] truncate">{branch?.Name || "Pilih Cabang"}</span>
            <ChevronDown className="w-3 h-3 shrink-0" />
          </button>
          <button
            onClick={() => nav("/search")}
            className="flex-1 min-w-0 flex items-center gap-2 bg-gray-100 hover:bg-gray-200 transition rounded-full px-3 py-2 text-sm text-gray-500"
            data-testid="header-search"
          >
            <Search className="w-4 h-4 shrink-0" />
            <span className="truncate">Cari produk favoritmu...</span>
          </button>
          <button
            onClick={() => nav("/cart")}
            className="relative shrink-0 p-2 rounded-full hover:bg-gray-100 transition"
            data-testid="header-cart"
          >
            <ShoppingCart className="w-5 h-5 text-gray-800" />
            {cartCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-[#D32F2F] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                {cartCount > 99 ? "99+" : cartCount}
              </span>
            )}
          </button>
        </div>
      </header>

      <main className="min-h-[calc(100vh-200px)]">
        <Outlet context={{ openAuth: () => setShowAuth(true), openBranch: () => setShowBranch(true) }} />
      </main>

      {/* Bottom nav */}
      {!hideBottomNav && (
        <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-gray-100 px-2 py-2 flex justify-around items-center z-40">
          {NAV.map(({ to, label, icon: Icon, testId, authRequired }) => {
            const active = loc.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                data-testid={testId}
                onClick={(e) => {
                  if (authRequired && !isAuthed) {
                    e.preventDefault();
                    setShowAuth(true);
                  }
                }}
                className={`flex flex-col items-center gap-0.5 py-1 px-2 rounded-xl transition ${active ? "text-[#D32F2F]" : "text-gray-500 hover:text-gray-700"}`}
              >
                <Icon className={`w-5 h-5 ${active ? "stroke-[2.4]" : ""}`} />
                <span className="text-[10px] font-medium">{label}</span>
              </Link>
            );
          })}
        </nav>
      )}

      <BranchModal open={showBranch} onClose={() => setShowBranch(false)} />
      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} />
      <TermsGate />

      {!isAuthed && !hideBottomNav && (
        <button
          onClick={() => setShowAuth(true)}
          className="hidden"
          data-testid="floating-login-button"
        >
          <LogIn className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
